import { computed } from "@preact/signals";

import { navigate, readUrl, search } from "~/util/router.js";

/** Signal-like accessors over well-known search parameters of the current route. */
export class SearchParams {
	/** The current page, zero-indexed. Stored one-indexed in `?page=`. */
	static #page = computed(() => {
		const page = Number.parseInt(search.value.page ?? "", 10) - 1;

		if (Number.isNaN(page)) return 0;
		return page;
	});
	static page = {
		get value() {
			return SearchParams.#page.value;
		},
		set value(page) {
			if (page === this.value) return;
			SearchParams.#navigate({ page: page === 0 ? undefined : `${page + 1}` });
		},
		/**
		 * @param {(value: number) => void} fn
		 * @returns {() => void}
		 */
		subscribe(fn) {
			return SearchParams.#page.subscribe(fn);
		},
	};

	/** The current search query, from `?q=`. Setting it also resets the page. */
	static #q = computed(() => search.value.q ?? "");
	static q = {
		get value() {
			return SearchParams.#q.value;
		},
		set value(q) {
			if (q === this.value) return;
			SearchParams.#navigate({ q, page: undefined }, { replace: true });
		},
		/**
		 * @param {(value: string) => void} fn
		 * @returns {() => void}
		 */
		subscribe(fn) {
			return SearchParams.#q.subscribe(fn);
		},
	};

	/**
	 * The current sort, from `?sort=` and `?order=`. The key may be missing or one the page's
	 * endpoint doesn't accept; an order other than `asc` or `desc` reads as missing. Setting it also
	 * resets the page.
	 */
	static #sortBy = computed(() => search.value.sort);
	static #sortOrder = computed(() => {
		const order = search.value.order;
		return order === "asc" || order === "desc" ? order : undefined;
	});
	static sort = {
		/** @returns {{ by: string | undefined; order: "asc" | "desc" | undefined }} */
		get value() {
			return { by: SearchParams.#sortBy.value, order: SearchParams.#sortOrder.value };
		},
		set value({ by, order }) {
			if (by === this.value.by && order === this.value.order) return;
			SearchParams.#navigate({ sort: by, order, page: undefined }, { replace: true });
		},
	};

	/**
	 * Navigates to the current URL with `params` set, removing the empty ones.
	 *
	 * @param {Record<string, string | undefined>} params
	 * @param {{ replace?: boolean }} [options]
	 */
	static #navigate(params, options) {
		const url = readUrl();
		for (const [key, value] of Object.entries(params)) {
			if (value) url.searchParams.set(key, value);
			else url.searchParams.delete(key);
		}
		navigate(url.pathname + url.search + url.hash, options);
	}
}
